
import { type Dispatch } from 'redux';

import { getCurrentConference } from '../base/conference';
import { openDialog } from '../base/dialog';
import { isLocalParticipantModerator } from '../base/participants';

import { ENABLE_LOBBY, HANDLE_SOUND, SET_LOBBY_LOADING, SET_LOBBY_MODE_ENABLED, SET_LOBBY_OVERRIDE_RECEIVED } from './actionTypes';
import { isEnablingLobbyAllowed } from './functions';

/**
 * Metadata key under which the authoritative room-level lobby decision is
 * stored. Using room metadata (as opposed to endpoint messages) means the
 * decision is persisted at the room level and delivered to every moderator,
 * including those who join or rejoin later.
 */
export const LOBBY_METADATA_KEY = 'lobby';

/**
 * Action to set the lobby toggle loading state.
 *
 * @param {boolean} loading - True if lobby toggle is currently loading.
 * @returns {{
 *     loading: boolean,
 *     type: SET_LOBBY_LOADING
 * }}
 */
export function setLobbyLoading(loading: boolean) {
    return {
        type: SET_LOBBY_LOADING,
        loading
    };
}

/**
 * Action to toggle lobby mode on or off.
 *
 * @param {boolean} enabled - The desired (new) state of the lobby mode.
 * @returns {Function}
 */
export function toggleLobbyMode(enabled: boolean) {
    return async (dispatch: Dispatch<any>, getState: Function) => {
        const conference = getCurrentConference(getState);

        dispatch(setLobbyLoading(true));
        setTimeout(() => {
            dispatch(setLobbyLoading(false));
        }, 2500);

        dispatch({
            enabled,

            // Flags this as an explicit moderator action so the reducer can
            // remember a manual "off" and prevent config-driven auto re-enable.
            manual: true,
            type: SET_LOBBY_MODE_ENABLED
        });

        if (enabled) {
            try {
                if (conference
                    && conference.isJoined()
                    && isLocalParticipantModerator(getState())
                    && isEnablingLobbyAllowed(getState())) {
                    conference.enableLobby(true);
                }
            } catch (e) {
                //
            }
        } else {
            try {
                if (conference && conference.isJoined() && isLocalParticipantModerator(getState())) {
                    // `enableLobby` ignores its argument and always enables the lobby
                    // (it calls getLobby().enable() unconditionally). To actually turn
                    // the lobby off we must call the dedicated `disableLobby()` method,
                    // otherwise members-only stays on and guests keep knocking.
                    conference.disableLobby();
                }
            } catch (e) {
                //
            }
        }

        // Persist the authoritative room-level lobby decision so it propagates to
        // every moderator — including those who join or rejoin later — and overrides
        // the JWT/prosody-driven default (the backend force-enables the lobby from a
        // joining moderator's JWT, which client gating alone cannot prevent).
        // Degrades gracefully when the backend room metadata module is unavailable.
        try {
            const metadataHandler = conference && typeof conference.getMetadataHandler === 'function'
                ? conference.getMetadataHandler()
                : undefined;

            if (metadataHandler
                    && typeof metadataHandler.isSupported === 'function'
                    && metadataHandler.isSupported()
                    && isLocalParticipantModerator(getState())) {
                metadataHandler.setMetadata(LOBBY_METADATA_KEY, { enabled });
                dispatch(setLobbyOverrideReceived(enabled));
            }
        } catch (e) {
            //
        }
    };
}


/**
 * Stores the authoritative room-level lobby decision received from room
 * metadata so late-joining moderators and the UI reflect the room creator's /
 * last moderator's choice.
 *
 * @param {boolean} override - The decided lobby state (true = on, false = off).
 * @returns {{
 *     override: boolean,
 *     type: SET_LOBBY_OVERRIDE_RECEIVED
 * }}
 */
export function setLobbyOverrideReceived(override: boolean) {
    return {
        override,
        type: SET_LOBBY_OVERRIDE_RECEIVED
    };
}

/**
 * Reads the authoritative lobby decision stored in room metadata (recorded by
 * the room creator / a moderator via {@link toggleLobbyMode}) and reconciles the
 * actual conference lobby (members-only) state to match it. This keeps the lobby
 * on/off decision consistent across all moderators, even when a late-joining
 * moderator's JWT (ENABLE_LOBBY) causes the backend to force-enable the lobby.
 *
 * @returns {Function} Thunk returning {@code true} when a room-level decision
 * exists (regardless of whether reconciliation was needed), {@code false}
 * otherwise.
 */
export function reconcileLobbyFromMetadata() {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();
        const conference = getCurrentConference(getState);

        if (!conference) {
            return false;
        }

        const metadataHandler = typeof conference.getMetadataHandler === 'function'
            ? conference.getMetadataHandler()
            : undefined;
        const metadata = metadataHandler && typeof metadataHandler.getMetadata === 'function'
            ? metadataHandler.getMetadata()
            : undefined;
        const decided = metadata && metadata[LOBBY_METADATA_KEY]
            ? metadata[LOBBY_METADATA_KEY].enabled
            : undefined;

        if (typeof decided !== 'boolean') {
            // No authoritative room-level lobby decision has been recorded yet.
            return false;
        }

        // Remember the decision so late-joining moderators and the UI reflect it.
        dispatch(setLobbyOverrideReceived(decided));

        // Only a moderator can change the members-only (lobby) state; reconcile the
        // actual server state to the decided state when they diverge. This heals the
        // case where the backend (prosody) force-enabled the lobby from a late-joining
        // moderator's JWT even though the room decision was to keep it off.
        if (!isLocalParticipantModerator(state)) {
            return true;
        }

        const actualEnabled = typeof conference.isMembersOnly === 'function'
            ? conference.isMembersOnly()
            : state['features/lobby'].lobbyEnabled;

        if (decided === actualEnabled) {
            return true;
        }

        try {
            if (decided) {
                if (conference.isJoined() && isEnablingLobbyAllowed(getState())) {
                    conference.enableLobby(true);
                }
            } else if (conference.isJoined()) {
                // `enableLobby` ignores its argument and always enables the lobby, so
                // we must call the dedicated `disableLobby()` to actually turn it off.
                conference.disableLobby();
            }
        } catch (e) {
            //
        }

        return true;
    };
}

/**
 * Action to toggle lobby mode on or off.
 *
 * @param {boolean} enabledLobby - The desired (new) state of the lobby mode.
 * @returns {Function}
 */
export function enableLobby(enabledLobby: boolean) {
    return {
        type: ENABLE_LOBBY,
        enabledLobby
    };
}

/**
 * Action to set the new state of the lobby mode.
 *
 * @param {boolean} soundBool - The new state to set.
 * @returns {{
 *     enabled: boolean,
 *     type: SET_LOBBY_MODE_ENABLED
 * }}
 */
export function StartSound(soundBool: boolean) {
    return {
        type: HANDLE_SOUND,
        soundBool
    };
}

/**
 * Opens a dialog to choose whether lobby sound preference applies to the
 * current moderator only or to all moderators.
 *
 * @returns {Object}
 */
export function showLobbySoundScopeDialog() {
    const LobbySoundScopeDialog = require('./components/LobbySoundScopeDialog').default;

    return openDialog(LobbySoundScopeDialog);
}


/**
 * Action used to admit multiple participants in the conference.
 *
 * @param {Array<Object>} participants - A list of knocking participants.
 * @returns {void}
 */
export function admitAllLobbyParticipants(participants: Array<Object>) {
    return (dispatch: Function, getState: Function) => {
        const conference = getCurrentConference(getState);

        participants.forEach(p => {
            conference.lobbyApproveAccess(p.id);
        });
    };
}
